/**
 * What one upload may be, as vfs-service says (GET /api/v1/upload/limits, or the project's own
 * /api/v1/projects/:id/upload/limits): `{ maxArchiveBytes, maxExtractedBytes, maxFiles, freeBytes }`. The plan, the
 * archive reader and the batches size themselves by it, so the web app never promises more than the server takes nor
 * refuses what it would take. A server without the endpoint (an older vfs-service) or one that cannot be reached: the
 * old constants (SERVER_LIMITS, 200 MB) with `source: 'fallback'`.
 */
import config from '../../config';
import { getAuthorizedFetch } from '../fetchRegistry';
import { SERVER_LIMITS } from './uploadPlan';

const positive = (value) => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null);

/** The server's answer laid over the fallback: each number it gives that makes sense, the old constant otherwise. */
export function limitsFrom(body) {
  if (!body || typeof body !== 'object') { return { ...SERVER_LIMITS, freeBytes: null, source: 'fallback' }; }
  const maxArchiveBytes = positive(body.maxArchiveBytes);
  if (!maxArchiveBytes) { return { ...SERVER_LIMITS, freeBytes: null, source: 'fallback' }; }
  return {
    maxArchiveBytes,
    maxExtractedBytes: positive(body.maxExtractedBytes) || SERVER_LIMITS.maxExtractedBytes,
    maxFiles: positive(body.maxFiles) || SERVER_LIMITS.maxFiles,
    freeBytes: typeof body.freeBytes === 'number' && Number.isFinite(body.freeBytes) && body.freeBytes >= 0 ? body.freeBytes : null,
    source: 'server',
  };
}

const cache = new Map();
const CACHE_MS = 60 * 1000;

/** Forgets the answers kept (tests; a new sandbox). */
export function resetServerLimits() {
  cache.clear();
}

/**
 * The limits for an upload into `projectId` (its owner's sandbox answers), or into the person's own sandbox without
 * one (a new project's import). Kept a minute; never throws.
 */
export async function fetchServerLimits({ projectId = null, fetchImpl = null } = {}) {
  const base = String(config.vfsServiceUrl || '').replace(/\/$/, '');
  if (!base) { return limitsFrom(null); }
  const url = projectId ? `${base}/api/v1/projects/${encodeURIComponent(projectId)}/upload/limits` : `${base}/api/v1/upload/limits`;
  const kept = cache.get(url);
  if (kept && Date.now() - kept.at < CACHE_MS) { return kept.limits; }
  let limits;
  try {
    const response = await (fetchImpl || getAuthorizedFetch())(url, { headers: { Accept: 'application/json' } });
    limits = limitsFrom(response.ok ? await response.json().catch(() => null) : null);
  } catch {
    limits = limitsFrom(null);
  }
  // A fallback is not kept: the next upload asks again (the sandbox may have been waking).
  if (limits.source === 'server') { cache.set(url, { at: Date.now(), limits }); }
  return limits;
}
